// Pure helpers for one parameter's history page (tap a Home card's number).
// Stats are computed on the stored values (ppt, °F), so "in band" compares
// like with like; every number SHOWN is in the keeper's units, through the
// same conversions All history uses: until 2026-10-01 this page showed a
// Celsius keeper 78.2°F and an S.G. keeper 35.2 ppt.
import { displayUnit, paramToDisplay, pointToDisplay } from './all-history.js';
import { pptToSg, formatSalinity, sgDecimals } from './salinity.js';
import { fToC, formatTemp } from './units.js';

export function computeHistoryStats(readings, p, now = Date.now()) {
  if (!readings || readings.length === 0) {
    return { last: null, prior24h: null, delta24h: null, inBandPct: null, min: null, max: null };
  }
  const newest = readings[0];
  const lastVal = Number(newest.value);
  // The reading at or before 24 h ago, to compare the newest one against.
  // Nothing logged in the last 24 h means there is no 24 h change to show:
  // a "0.00" there read as rock steady when it meant no data.
  const cutoff = now - 24 * 3_600_000;
  const newestTs = new Date(newest.ts).getTime();
  let prior = null;
  if (Number.isFinite(newestTs) && newestTs > cutoff) {
    prior = readings.slice(1).find((r) => new Date(r.ts).getTime() <= cutoff) || null;
  }
  const delta24h = prior ? lastVal - Number(prior.value) : null;
  let inBandPct = null;
  if (Array.isArray(p?.acceptable) && p.acceptable.length === 2) {
    const [lo, hi] = p.acceptable.map(Number);
    if (Number.isFinite(lo) && Number.isFinite(hi)) {
      const vals = readings.map((r) => Number(r.value)).filter(Number.isFinite);
      if (vals.length) {
        const inside = vals.filter((v) => v >= lo && v <= hi).length;
        inBandPct = Math.round((inside / vals.length) * 100);
      }
    }
  }
  const vals = readings.map((r) => Number(r.value)).filter(Number.isFinite);
  const min = vals.length ? Math.min(...vals) : null;
  const max = vals.length ? Math.max(...vals) : null;
  return {
    last: lastVal,
    prior24h: prior ? Number(prior.value) : null,
    delta24h,
    inBandPct,
    min,
    max,
  };
}

// The stats strip for the range picked: range and in-band from the
// readings in that range, the 24h change from every reading loaded, since
// the 24h range holds no reading from a day ago and always showed "—".
export function historyStatsForRange(inRange, all, p, now = Date.now()) {
  const shown = computeHistoryStats(inRange, p, now);
  const day = computeHistoryStats(all, p, now);
  return { ...shown, prior24h: day.prior24h, delta24h: day.delta24h };
}

// Formats a STORED value for the screen, in the keeper's units and at the
// precision the Home card prints it (formatCardValue, formatTemp, and for
// S.G. the card's sgDecimals against `sgBand`): the card read 0.147 and
// 1.0272 S.G. HIGH while this page said 0.15 and 1.027.
export function makeHistoryFormat(key, storedUnit, opts) {
  const salUnit = opts?.salinityUnit || 'ppt';
  const sg = key === 'salinity' && salUnit === 'sg';
  const tempC = key === 'temp' && opts?.tempUnit === 'C';
  const unit = displayUnit(key, storedUnit, opts);
  const finite = (v) => v != null && v !== '' && Number.isFinite(Number(v));
  const places = (v) =>
    Math.max(opts?.sgDecimals ?? 3, opts?.sgBand ? sgDecimals(opts.sgBand, v) : 3);
  const value = (v) => {
    if (!finite(v)) return '—';
    if (key === 'salinity') return String(formatSalinity(v, salUnit, sg ? places(v) : undefined));
    if (tempC) return formatTemp(v, 'C', { withUnit: false });
    return formatCardValue(key, v);
  };
  return {
    unit,
    value,
    // A change between two stored values, in display units.
    delta: (from, to) => {
      if (!finite(from) || !finite(to)) return null;
      const a = Number(from);
      const b = Number(to);
      let diff;
      let text;
      if (sg) {
        const d = Math.max(places(a), places(b));
        diff = pptToSg(b, d) - pptToSg(a, d);
        text = Math.abs(diff).toFixed(d);
      } else if (key === 'salinity') {
        diff = formatSalinity(b, salUnit) - formatSalinity(a, salUnit);
        text = Math.abs(diff).toFixed(1);
      } else if (tempC) {
        diff = fToC(b) - fToC(a);
        text = Math.abs(diff).toFixed(1);
      } else {
        diff = b - a;
        text = formatCardValue(key, Math.abs(diff));
      }
      const arrow = Number(text) === 0 ? '→' : diff > 0 ? '↑' : '↓';
      return `${arrow} ${text}${unit ? ` ${unit}` : ''}`;
    },
  };
}

// The param as the chart should draw it, in display units. An S.G. band
// keeps a fourth decimal so 34.7-35.3 ppt shades a band, not a line.
export function historyChartParam(key, p, opts) {
  const shown = paramToDisplay(key, p, opts);
  if (
    shown &&
    key === 'salinity' &&
    opts?.salinityUnit === 'sg' &&
    Array.isArray(p?.acceptable) &&
    p.acceptable.length === 2
  ) {
    const d = Math.max(4, opts?.sgDecimals ?? 4);
    return {
      ...shown,
      acceptable: p.acceptable.map((v) => pptToSg(v, d)),
      ...(opts?.sgDecimals ? { decimals: opts.sgDecimals } : {}),
    };
  }
  return shown;
}

export function readingsToDisplay(key, readings, opts) {
  return (readings || []).map((r) => pointToDisplay(key, r, opts));
}

// Most decimals a Home card prints per parameter, in stored units. A probe
// or an ICP import stores what it was sent ("436.57 ppm", "78.16"), which
// reads as a precision no kit has; trailing zeros are dropped, so a typed
// "0.25" ammonia or "8" dKH shows as typed. Unlisted params keep up to 3.
const CARD_DECIMALS = {
  alk: 1,
  calcium: 0,
  magnesium: 0,
  nitrate: 2,
  phosphate: 3,
  ph: 2,
  salinity: 1,
  temp: 1,
};

export function formatCardValue(key, value) {
  const n = Number(value);
  if (value == null || value === '' || !Number.isFinite(n))
    return value == null ? '' : String(value);
  const d = CARD_DECIMALS[key] ?? 3;
  return String(Number(n.toFixed(d)));
}
